<script lang="ts" context="module">
    import Modal, { createModal } from '$components/Modal.svelte';

    export { createModal };
</script>

<script lang="ts">
    import { fade } from 'svelte/transition';
    import WithTooltip from '$components/Tooltip/WithTooltip.svelte';
    import MediaImage from '$components/MediaImage/MediaImage.svelte';
    import MediaImageUrl from '$components/MediaImage/MediaImageUrl.svelte';
    import { cx } from '$lib/utils/cx.js';
    import { quadInOut, quadOut } from 'svelte/easing';
    import findIndex from 'lodash/findIndex';
    import { melt } from '@melt-ui/svelte';

    export let modal: ReturnType<typeof createModal>;
    const {
        elements: { close },
        states: { open },
    } = modal;

    let hovered = false;

    export let mediaIds: number[];
    let currentIndex = 0;

    $: currentMediaId = mediaIds[currentIndex] ?? null;

    $: nextMediaId = mediaIds[currentIndex + 1] ?? null;
    $: hasNext = nextMediaId !== null;

    $: prevMediaId = mediaIds[currentIndex - 1] ?? null;
    $: hasPrev = prevMediaId !== null;

    function fadeIn(node: Element) {
        return fade(node, {
            duration: 300,
            easing: quadOut,
        });
    }

    function fadeOut(node: Element) {
        return fade(node, {
            duration: 300,
            easing: quadInOut,
        });
    }

    function onPrev() {
        currentIndex = Math.max(currentIndex - 1, 0);
    }

    function onNext() {
        currentIndex = Math.min(currentIndex + 1, mediaIds.length - 1);
    }

    export function selectMediaId(mediaId: number) {
        currentIndex = findIndex(mediaIds, (id) => id === mediaId);
    }

    function onKeyDown(event: KeyboardEvent) {
        const target = event.target as Element;
        const tagName = target.tagName.toLowerCase();

        if ($open && tagName !== 'input' && tagName !== 'textarea') {
            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                onPrev();
            } else if (event.key === 'ArrowRight') {
                event.preventDefault();
                onNext();
            }
        }
    }
</script>

<svelte:body on:keydown={onKeyDown} />

<Modal
    dialogClass="b:p-0"
    contentClass="w-[100vw] h-[100vh] ls:w-[calc(100vw-56px)] ls:h-[calc(100vh-56px)] bg-gray-900"
    {modal}
>
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div
        on:mouseenter={() => (hovered = true)}
        on:mouseleave={() => (hovered = false)}
        class="w-full h-full relative"
    >
        {#if hovered}
            <div
                in:fadeIn
                out:fadeOut
                class="absolute top-0 left-0 right-0 z-2 h-[40px] bg-gradient-to-b from-black to-black/0 flex justify-end items-center"
            >
                <button
                    use:melt={$close}
                    class="btn-reset hover:text-white/90 text-white/70 text-4xl p-4"
                >
                    <span aria-hidden="true">&times;</span>
                </button>
            </div>
        {/if}

        <div class="flex items-center justify-center w-full h-full">
            {#if currentMediaId != null}
                <MediaImageUrl mediaId={currentMediaId}>
                    <div
                        in:fadeIn
                        out:fadeOut
                        slot="default"
                        let:urlMedia
                        class={'absolute -inset-[100px] border-gray-700 bg-black bg-center bg-cover blur-2xl brightness-50'}
                        style:background-image={`url("${urlMedia('reference')}")`}
                    />
                </MediaImageUrl>
                <MediaImage
                    mediaId={currentMediaId}
                    format="reference"
                    class="object-contain w-full h-full z-1"
                />
            {/if}
        </div>

        {#if hasPrev && hovered}
            <WithTooltip
                tooltip="Précédent"
                openDelay={0}
                closeDelay={0}
                positioning={{ placement: 'right' }}
            >
                <button
                    slot="default"
                    let:trigger
                    use:melt={trigger}
                    in:fadeIn
                    out:fadeOut
                    on:click|preventDefault={onPrev}
                    class={cx(
                        'z-2 absolute top-[calc(50%-20px)] left-[20px] transition-default',
                        'btn-reset bg-gray-200 hover:bg-white rounded-full shadow h-[40px] w-[40px] flex items-center justify-center border border-solid border-gray-400',
                    )}
                >
                    <i class="tnp tnp-arrow rotate-180" />
                </button>
            </WithTooltip>
        {/if}

        {#if hasNext && hovered}
            <WithTooltip
                tooltip="Suivant"
                openDelay={0}
                closeDelay={0}
                positioning={{ placement: 'right' }}
            >
                <button
                    slot="default"
                    let:trigger
                    use:melt={trigger}
                    in:fadeIn
                    out:fadeOut
                    on:click|preventDefault={onNext}
                    class={cx(
                        'z-2 absolute top-[calc(50%-20px)] right-[20px] transition-default',
                        'btn-reset bg-gray-200 hover:bg-white rounded-full shadow h-[40px] w-[40px] flex items-center justify-center border border-solid border-gray-400',
                    )}
                >
                    <i class="tnp tnp-arrow" />
                </button>
            </WithTooltip>
        {/if}

        {#if hovered}
            <div
                in:fadeIn
                out:fadeOut
                class="z-2 absolute left-0 bottom-0 right-0 h-[40px] bg-gradient-to-t from-black to-black/0 flex justify-end items-center transition-default px-5"
            >
                <WithTooltip tooltip="Reset" openDelay={0} closeDelay={0}>
                    <button
                        slot="default"
                        let:trigger
                        use:melt={trigger}
                        class="btn-reset hover:text-white/90 text-white/70 text-2xl px-4 py-3"
                    >
                        <i class="fa fa-compress" />
                    </button>
                </WithTooltip>

                <WithTooltip tooltip="Zoom -" openDelay={0} closeDelay={0}>
                    <button
                        slot="default"
                        let:trigger
                        use:melt={trigger}
                        class="btn-reset hover:text-white/90 text-white/70 text-2xl px-4 py-3"
                    >
                        <i class="fa fa-search-minus" />
                    </button>
                </WithTooltip>

                <WithTooltip tooltip="Zoom +" openDelay={0} closeDelay={0}>
                    <button
                        slot="default"
                        let:trigger
                        use:melt={trigger}
                        class="btn-reset hover:text-white/90 text-white/70 text-2xl px-4 py-3"
                    >
                        <i class="fa fa-search-plus" />
                    </button>
                </WithTooltip>

                <WithTooltip
                    tooltip="Faire pivoter à gauche"
                    openDelay={0}
                    closeDelay={0}
                >
                    <button
                        slot="default"
                        let:trigger
                        use:melt={trigger}
                        class="btn-reset hover:text-white/90 text-white/70 text-2xl px-4 py-3"
                    >
                        <i class="fa fa-rotate-left" />
                    </button>
                </WithTooltip>

                <WithTooltip
                    tooltip="Faire pivoter à droite"
                    openDelay={0}
                    closeDelay={0}
                >
                    <button
                        slot="default"
                        let:trigger
                        use:melt={trigger}
                        class="btn-reset hover:text-white/90 text-white/70 text-2xl px-4 py-3"
                    >
                        <i class="fa fa-rotate-right" />
                    </button>
                </WithTooltip>

                <WithTooltip tooltip="Télécharger" openDelay={0} closeDelay={0}>
                    <button
                        slot="default"
                        let:trigger
                        use:melt={trigger}
                        class="btn-reset hover:text-white/90 text-white/70 text-2xl px-4 py-3"
                    >
                        <i class="fa fa-cloud-download" />
                    </button>
                </WithTooltip>

                <WithTooltip tooltip="Ouvrir dans un onglet" openDelay={0} closeDelay={0}>
                    <button
                        slot="default"
                        let:trigger
                        use:melt={trigger}
                        class="btn-reset hover:text-white/90 text-white/70 text-2xl px-4 py-3"
                    >
                        <i class="fa fa-clone" />
                    </button>
                </WithTooltip>
            </div>
        {/if}
    </div>
</Modal>
